"use client";

import Link from "@/infrastructure/router/Link";
import { useCallback, useEffect, useState } from "react";
import { GlassButton } from "@/components/design-system/buttons/GlassButton";
import { GlassCard } from "@/components/design-system/cards/GlassCard";
import { ArtistIcon } from "@/components/design-system/icons/ArtistIcon";
import { EmptyState } from "@/components/design-system/states/EmptyState";
import type {
	PromotionCampaign,
	PromotionClaimResult,
	PromotionCommand,
	PromotionDashboard as PromotionDashboardValue,
	PromotionPaybackOffer,
} from "@/domain/promotion/Promotion";
import { artistSupabase } from "@/infrastructure/supabase/client";
import { useTranslation } from "@/i18n/ArtistI18n";

type PromotionSection = "tasks" | "payback";

async function requestDashboard(): Promise<PromotionDashboardValue> {
	const { data, error } = await artistSupabase().rpc("get_promotion_dashboard");
	if (error) throw error;
	return data as PromotionDashboardValue;
}

async function sendCommand(command: PromotionCommand): Promise<PromotionClaimResult> {
	const supabase = artistSupabase();
	const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
	if (sessionError || sessionData.session === null) {
		throw new Error("promotion-authentication-required");
	}
	let result: { data: unknown; error: { message: string } | null };
	switch (command.action) {
		case "claim-campaign":
			result = await supabase.rpc("claim_promotion_reward", {
				p_campaign_key: command.campaignKey,
			});
			break;
		case "claim-daily":
			result = await supabase.rpc("claim_daily_promotion_coins");
			break;
		case "claim-payback":
			result = await supabase.rpc("claim_credit_payback", {
				p_offer_id: command.offerId,
			});
			break;
		case "record-event":
			result = await supabase.rpc("record_promotion_event", {
				p_event_key: command.eventKey,
				p_distinct_ref: command.distinctReference,
				p_payload: command.payload,
			});
			break;
	}
	if (result.error) throw new Error(result.error.message);
	return result.data as PromotionClaimResult;
}

export function PromotionDashboard() {
	const { t } = useTranslation();
	const [section, setSection] = useState<PromotionSection>("tasks");
	const [dashboard, setDashboard] = useState<PromotionDashboardValue | null>(null);
	const [loading, setLoading] = useState(true);
	const [activeCommand, setActiveCommand] = useState<string | null>(null);
	const [notice, setNotice] = useState<string | null>(null);

	const load = useCallback(async () => {
		setLoading(true);
		try {
			setDashboard(await requestDashboard());
		} catch {
			setNotice(t("promotion.loadFailed"));
		} finally {
			setLoading(false);
		}
	}, [t]);

	useEffect(() => {
		void load();
	}, [load]);

	const run = useCallback(
		async (id: string, command: PromotionCommand) => {
			setActiveCommand(id);
			try {
				const result = await sendCommand(command);
				setNotice(result.ok ? t("promotion.claimSucceeded") : t("promotion.loadFailed"));
				await load();
			} catch (error) {
				setNotice(
					error instanceof Error && error.message === "promotion-authentication-required"
						? t("promotion.authRequired")
						: t("promotion.loadFailed"),
				);
			} finally {
				setActiveCommand(null);
			}
		},
		[load, t],
	);

	return (
		<div className="promotion-page">
			<header className="promotion-page__header">
				<Link className="ds-icon-button" href="/discover" aria-label={t("common.close")}>
					<ArtistIcon name="back" size={20} />
				</Link>
				<h1>{t("promotion.title")}</h1>
				<span className="promotion-page__header-spacer" aria-hidden="true" />
			</header>

			<main className="promotion-page__content">
				<section className="promotion-hero">
					<ArtistIcon name="generate" size={36} />
					<h2 className="ds-font-genart">
						{dashboard?.campaign?.title ?? t("promotion.hero.title")}
					</h2>
					<p>{t("promotion.hero.subtitle")}</p>
				</section>

				<div className="promotion-tabs" role="tablist" aria-label={t("promotion.title")}>
					<GlassButton
						kind={section === "tasks" ? "primary" : "secondary"}
						size="sm"
						role="tab"
						aria-selected={section === "tasks"}
						onClick={() => setSection("tasks")}
					>
						{t("promotion.tasks")}
					</GlassButton>
					<GlassButton
						kind={section === "payback" ? "primary" : "secondary"}
						size="sm"
						role="tab"
						aria-selected={section === "payback"}
						onClick={() => setSection("payback")}
					>
						{t("promotion.payback")}
					</GlassButton>
				</div>

				{loading && dashboard === null ? (
					<div className="promotion-loading" aria-live="polite">
						<span className="ds-spinner" aria-hidden="true" />
						<span>{t("promotion.title")}</span>
					</div>
				) : section === "tasks" ? (
					<PromotionTasks
						dashboard={dashboard}
						activeCommand={activeCommand}
						onClaimCampaign={(campaign) =>
							void run(`campaign:${campaign.campaign_key}`, {
								action: "claim-campaign",
								campaignKey: campaign.campaign_key,
							})
						}
						onClaimDaily={() => void run("daily", { action: "claim-daily" })}
					/>
				) : (
					<PromotionPayback
						dashboard={dashboard}
						activeCommand={activeCommand}
						onClaim={(offer) =>
							void run(`payback:${offer.id}`, {
								action: "claim-payback",
								offerId: offer.id,
							})
						}
					/>
				)}
			</main>

			{notice === null ? null : (
				<div className="promotion-notice" role="alertdialog" aria-modal="true">
					<GlassCard className="promotion-notice__card">
						<p>{notice}</p>
						<GlassButton kind="primary" expands onClick={() => setNotice(null)}>
							{t("common.ok")}
						</GlassButton>
					</GlassCard>
				</div>
			)}
		</div>
	);
}

function PromotionTasks({
	dashboard,
	activeCommand,
	onClaimCampaign,
	onClaimDaily,
}: Readonly<{
	dashboard: PromotionDashboardValue | null;
	activeCommand: string | null;
	onClaimCampaign: (campaign: PromotionCampaign) => void;
	onClaimDaily: () => void;
}>) {
	const { t } = useTranslation();
	if (dashboard === null) {
		return (
			<EmptyState
				title={t("promotion.loadFailed")}
				message={t("common.retry")}
				symbol={<ArtistIcon name="info" size={32} />}
			/>
		);
	}
	return (
		<div className="promotion-stack">
			{dashboard.campaign === null ? null : (
				<GlassCard className="promotion-card">
					<div className="promotion-card__heading">
						<div>
							<h3>{dashboard.campaign.title}</h3>
							<p>
								{dashboard.campaign.completed_task_count}/
								{dashboard.campaign.required_task_count} {t("promotion.completed")}
							</p>
						</div>
						<strong>{dashboard.campaign.reward_credits}</strong>
					</div>
					<div className="promotion-task-list">
						{dashboard.tasks.map((task) => (
							<div className="promotion-task" key={task.id}>
								<ArtistIcon name={task.completed ? "check" : "circle"} size={20} />
								<div className="promotion-task__body">
									<strong>{task.title}</strong>
									<progress
										value={Math.min(task.progress_count, task.target_count)}
										max={Math.max(task.target_count, 1)}
									/>
								</div>
								<span>{task.progress_count}/{task.target_count}</span>
							</div>
						))}
					</div>
					{dashboard.campaign.reward_claimed || dashboard.campaign.reward_credits <= 0 ? null : (
						<GlassButton
							kind="primary"
							size="lg"
							expands
							isLoading={activeCommand === `campaign:${dashboard.campaign.campaign_key}`}
							isEnabled={!dashboard.authenticated || dashboard.campaign.can_claim}
							onClick={() => onClaimCampaign(dashboard.campaign as PromotionCampaign)}
						>
							{dashboard.authenticated
								? t("promotion.claimCredits", { credits: dashboard.campaign.reward_credits })
								: t("promotion.signInStart")}
						</GlassButton>
					)}
				</GlassCard>
			)}

			{dashboard.daily === null ? null : (
				<GlassCard className="promotion-card">
					<div className="promotion-card__heading">
						<h3>{t("promotion.daily.title")}</h3>
						<strong>{dashboard.daily.coins_today}/5</strong>
					</div>
					<progress value={dashboard.daily.wallet_coins} max={Math.max(dashboard.daily.coins_per_suno_call, 1)} />
					<p>
						{t("promotion.daily.wallet", {
							wallet: dashboard.daily.wallet_coins,
							target: dashboard.daily.coins_per_suno_call,
							credits: dashboard.daily.credits_per_suno_call,
						})}
					</p>
					<GlassButton
						kind="primary"
						expands
						isLoading={activeCommand === "daily"}
						isEnabled={!dashboard.daily.claimed_today && dashboard.daily.coins_today > 0}
						onClick={onClaimDaily}
					>
						{dashboard.daily.claimed_today
							? t("promotion.daily.claimed")
							: t("promotion.daily.claim")}
					</GlassButton>
				</GlassCard>
			)}
		</div>
	);
}

function PromotionPayback({
	dashboard,
	activeCommand,
	onClaim,
}: Readonly<{
	dashboard: PromotionDashboardValue | null;
	activeCommand: string | null;
	onClaim: (offer: PromotionPaybackOffer) => void;
}>) {
	const { t } = useTranslation();
	if (dashboard?.authenticated !== true) {
		return (
			<GlassCard className="promotion-card promotion-card--centered">
				<ArtistIcon name="card" size={32} />
				<h3>{t("promotion.payback.signIn")}</h3>
				<p>{t("promotion.payback.signInBody")}</p>
				<Link className="ds-button ds-button--primary ds-button--md" href="/auth">
					{t("settings.login.action")}
				</Link>
			</GlassCard>
		);
	}
	return (
		<div className="promotion-stack">
			<GlassCard className="promotion-card promotion-card--accent">
				<h3>{t("promotion.payback.explanationTitle")}</h3>
				<p>{t("promotion.payback.explanation")}</p>
			</GlassCard>
			{dashboard.payback_cycles.map((cycle) => (
				<GlassCard className="promotion-card" key={cycle.grant_id}>
					<div className="promotion-card__heading">
						<div>
							<h3>{cycle.plan_name}</h3>
							<p>{cycle.spent_credits}/{cycle.monthly_credits}</p>
						</div>
						<strong>{cycle.spend_percent}%</strong>
					</div>
					<progress value={cycle.spent_credits} max={Math.max(cycle.monthly_credits, 1)} />
					<div className="promotion-milestones">
						{cycle.milestones.map((milestone) => (
							<span
								className={cycle.spend_percent >= milestone.threshold_percent ? "is-complete" : ""}
								key={`${cycle.grant_id}:${milestone.threshold_percent}`}
							>
								{milestone.threshold_percent}% → {milestone.reward_percent}%
							</span>
						))}
					</div>
				</GlassCard>
			))}
			{dashboard.payback_offers.map((offer) => (
				<GlassCard className="promotion-card promotion-offer" key={offer.id}>
					<ArtistIcon name="gift" size={24} />
					<div>
						<strong>
							{t("promotion.payback.offer", {
								threshold: offer.spend_threshold_percent,
								credits: offer.reward_credits,
							})}
						</strong>
						<p>{offer.expires_at.slice(0, 10)}</p>
					</div>
					{offer.available ? (
						<GlassButton
							kind="primary"
							size="sm"
							isLoading={activeCommand === `payback:${offer.id}`}
							onClick={() => onClaim(offer)}
						>
							{t("promotion.claim")}
						</GlassButton>
					) : null}
				</GlassCard>
			))}
		</div>
	);
}
